import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { useTranslation } from "react-i18next";

const REQUIRED_HINTS = new Set([
  "cieloCheckout",
  "itauPix",
  "buspay",
  "pagarme",
  "adyen",
]);

const HINT_STEPS = {
  cieloCheckout: ["step1", "step2", "step3"],
  cieloEcommerce: ["step1", "step2"],
  itauPix: ["step1", "step2"],
  buspay: ["step1", "step2"],
  pagarme: ["step1", "step2"],
  pagueFacil: ["step1", "step2"],
  adyen: ["step1", "step2"],
  mercadoPago: ["step1"],
  appmax: ["step1"],
};

function resolveRegistration(notification) {
  const explicit = notification?.registration;
  if (explicit === "required" || explicit === "optional") return explicit;
  const hintKey = notification?.hintKey || "";
  return REQUIRED_HINTS.has(hintKey) ? "required" : "optional";
}

/**
 * Bloco em cascata: só aparece depois do tipo. URL read-only + copy + instruções do provedor.
 */
export default function PaymentMethodNotificationPanel({
  notification,
  showPassengerNote = false,
}) {
  const { t } = useTranslation("settings");
  const [copied, setCopied] = useState(false);

  if (!notification || !notification.needed) return null;

  const hintKey = notification.hintKey || "";
  const ready = !!notification.ready && !!notification.url;
  const registration = resolveRegistration(notification);
  const isRequired = registration === "required";
  const ns = `paymentMethodsPage.notification.${hintKey}`;
  const hasProviderCopy = Boolean(HINT_STEPS[hintKey]);

  const copyUrl = async () => {
    if (!notification.url) return;
    try {
      await navigator.clipboard.writeText(notification.url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  const steps = (HINT_STEPS[hintKey] || []).map((step) => t(`${ns}.${step}`));
  const intro = hasProviderCopy ? t(`${ns}.intro`) : null;

  return (
    <Paper variant="outlined" sx={{ p: 2, bgcolor: "action.hover" }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle2">
            {t("paymentMethodsPage.notification.title")}
          </Typography>
          {ready && hintKey !== "adapterUnavailable" && hintKey !== "comingSoon" ? (
            <Chip
              size="small"
              color={isRequired ? "warning" : "default"}
              label={
                isRequired
                  ? t("paymentMethodsPage.notification.requiredBadge")
                  : t("paymentMethodsPage.notification.optionalBadge")
              }
            />
          ) : null}
        </Stack>

        {hintKey === "adapterUnavailable" ? (
          <Alert severity="info">{t("paymentMethodsPage.notification.adapterUnavailable")}</Alert>
        ) : null}

        {hintKey === "comingSoon" ? (
          <Alert severity="info">{t("paymentMethodsPage.notification.comingSoon")}</Alert>
        ) : null}

        {ready ? (
          <>
            {intro ? (
              <Typography variant="body2" color="text.secondary">
                {intro}
              </Typography>
            ) : null}
            <TextField
              value={notification.url}
              fullWidth
              size="small"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      size="small"
                      onClick={copyUrl}
                      startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
                    >
                      {copied
                        ? t("paymentMethodsPage.notification.copied")
                        : t("paymentMethodsPage.notification.copy")}
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
            {steps.length ? (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                  {isRequired
                    ? t("paymentMethodsPage.notification.howToRequired")
                    : t("paymentMethodsPage.notification.howToOptional")}
                </Typography>
                <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
                  {steps.map((s) => (
                    <Typography key={s} component="li" variant="body2" color="text.secondary">
                      {s}
                    </Typography>
                  ))}
                </Box>
              </Box>
            ) : null}
            {showPassengerNote ? (
              <Alert severity="info" sx={{ py: 0.5 }}>
                {t("paymentMethodsPage.notification.bpeNote")}
              </Alert>
            ) : null}
          </>
        ) : hintKey !== "adapterUnavailable" && hintKey !== "comingSoon" ? (
          <Alert severity="warning">{t("paymentMethodsPage.notification.unavailableHost")}</Alert>
        ) : null}
      </Stack>
    </Paper>
  );
}
